import React, {memo, useMemo} from 'react';
import {Flex, Select, Text} from '@priority-software/priority-style-react';
import classNames from 'classnames';

import {DatePicker as ReactDatePicker} from '../../../../../../components/commonOldtoNew';
import {getLang} from '../../../../../../Localization';
import {
    getStringFormatByColumnType,
    isDateTime as getIsDateTime
} from '../../../../../../features/Form/utils/date';
import styles from '../../styles.module.scss';
import {Operator} from '../../../../../../_legacy/Searches/consts/operators';
import {ISearchCondition} from '../../../../types/filters';
import {getDateRanges, getDateRangesMap} from '../../../../../../common/dateRange';
import {ISearchField} from '../../../../../../common/searchType';
import {getLocalizedText, texts} from '../../../../../../constants/TextConst';
import {getFormattedDates} from '../../../../../../utils/getFormattedDates';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {
    getValidSearchFilterValues,
    prepareDateFilterValue
} from '../../../../../Form/actions/utils/filter';

interface Props {
    filter: ISearchCondition;
    column: ISearchField;
    onUpdate: (data: {date: string; dynamicDate: string}[]) => void;
    isDisabled: boolean;
}

const wildcard = /[*?]/;
const lang = getLang();

const DateControl = memo(({filter, column, onUpdate, isDisabled}: Props) => {
    const {operator} = filter;
    const {type} = column;

    const filterValues = useMemo(() => {
        /*
         * We need to map `dateEnum` to valid date values in order to handle dynamic dates correctly
         * and display them properly in the date picker.
         *  */
        return getValidSearchFilterValues(filter.values, filter.dateEnum);
    }, [filter.dateEnum, filter.values]);
    const dates = operator === Operator.Between ? filterValues : [filterValues[0]];

    const createUpdateHandler = (isMax: boolean) => (value: string) => {
        const dateRanges = getDateRangesMap();
        const index = isMax ? 1 : 0;
        const oppositeIndex = 1 - index;

        const opposite = {
            date: filter.values?.[oppositeIndex] || '',
            dynamicDate: filter.dateEnum?.[oppositeIndex] || ''
        };

        const isShortcut = wildcard.test(value);
        const isDynamic = value.length === 3 && !isShortcut;
        const dateObj = isDynamic ? dateRanges[value]?.getDate() : new Date(value);

        const updated = {
            dynamicDate: isDynamic ? value : '',
            date: isShortcut ? value : prepareDateFilterValue(dateObj) // We should always return a valid formatted date string or shortcut
        };

        const updatedValues = index === 1 ? [opposite, updated] : [updated, opposite];
        onUpdate(updatedValues);
    };

    return (
        <Flex
            {...createDataTestId('date-control-filter-box', true)}
            direction="column"
            componentClasses={classNames('date-picker', styles.pickersWrapper)}
        >
            <Flex alignItems="flex-start" componentClasses={styles.dateBetweenWrapper}>
                {operator === Operator.Between && (
                    <Text componentClasses={styles.datePickerText}>
                        {getLocalizedText(texts.from)}
                    </Text>
                )}
                <DatePicker
                    dateEnum={filter.dateEnum}
                    isDisabled={isDisabled}
                    dates={dates}
                    isStart={true}
                    type={type}
                    onChange={createUpdateHandler(false)}
                />
            </Flex>

            {operator === Operator.Between && (
                <Flex alignItems="flex-start" componentClasses={styles.dateBetweenWrapper}>
                    <Text componentClasses={styles.datePickerText}>
                        {getLocalizedText(texts.to)}
                    </Text>
                    <DatePicker
                        dateEnum={filter.dateEnum}
                        isStart={false}
                        dates={dates}
                        type={type}
                        onChange={createUpdateHandler(true)}
                        isDisabled={isDisabled}
                    />
                </Flex>
            )}
        </Flex>
    );
});

const DatePicker = ({dateEnum, type, onChange, dates, isStart, isDisabled = false}) => {
    const valueIndex = isStart ? 0 : 1;
    const date = getFormattedDates(dates, type, dateEnum)[valueIndex];
    const customRange = {value: 'custom', label: getLocalizedText(texts.customRange)};
    const dateRanges = getDateRangesMap();
    const rangeValue = dateEnum ? dateEnum[valueIndex] : dates[valueIndex];
    const dateRange = date ? dateRanges[rangeValue] || customRange : '';

    const format = getStringFormatByColumnType(type, lang);
    const isDateTime = getIsDateTime(type);

    const handleDateChange = (date: Date | string) => {
        const value = typeof date === 'string' ? date : date?.toISOString();
        onChange(value || '');
    };

    const handleRangeChange = (value: any) => {
        onChange(value.value);
    };

    return (
        <Flex
            componentClasses={styles.pickerWrapper}
            {...createDataTestId('date-picker-container', true)}
        >
            <Select
                size="medium"
                options={getDateRanges()}
                onChange={handleRangeChange}
                isSearchable={false}
                placeholder={null}
                componentClasses={classNames('date-range', styles.rangeSelect)}
                value={dateRange}
                menuScrollMaxHeight={240}
                isDisabled={isDisabled}
            />
            <div className={styles.datePicker} {...createDataTestId('react-date-picker', false)}>
                <ReactDatePicker
                    className={styles.picker}
                    dateFormat={format}
                    selected={date}
                    selectsStart={isStart}
                    selectsEnd={!isStart}
                    startDate={dates[0]}
                    endDate={dates[1]}
                    onChange={handleDateChange}
                    calendarClassName="custom"
                    showTimeSelect={isDateTime}
                    disabled={isDisabled}
                    timeFormat={format.slice(9)}
                />
            </div>
        </Flex>
    );
};

export default DateControl;
